import { h, clear } from '../dom.js';
import type { Sentence } from '../types.js';
import { formatTime } from '../audio.js';

export interface SentenceListHandle {
  root: HTMLElement;
  setSentences: (list: Sentence[], onPick: (index: number) => void) => void;
  setActive: (index: number) => void;
}

export function createSentenceList(): SentenceListHandle {
  const root = h('ol', { className: 'sl' });
  let rows: HTMLLIElement[] = [];
  let activeIdx = -1;

  function setSentences(list: Sentence[], onPick: (index: number) => void): void {
    clear(root);
    rows = list.map((s, i) => {
      const row = h('li', {
        className: 'sl__row',
        attrs: { role: 'button', tabindex: 0, 'data-index': i },
        on: {
          click: () => onPick(i),
          keydown: (e) => {
            const ke = e as KeyboardEvent;
            if (ke.key === 'Enter' || ke.key === ' ') {
              ke.preventDefault();
              onPick(i);
            }
          },
        },
      }, [
        h('span', { className: 'sl__idx', text: String(i + 1).padStart(2, '0') }),
        h('div', { className: 'sl__jp', text: s.text, attrs: { title: s.text } }),
        h('span', { className: 'sl__time', text: formatTime(s.start) }),
      ]);
      return row as HTMLLIElement;
    });
    for (const r of rows) root.appendChild(r);
    activeIdx = -1;
  }

  function setActive(index: number): void {
    if (index === activeIdx) return;
    if (activeIdx >= 0 && rows[activeIdx]) rows[activeIdx]!.classList.remove('sl__row--active');
    activeIdx = index;
    if (index >= 0 && rows[index]) {
      const row = rows[index]!;
      row.classList.add('sl__row--active');
      const rowRect = row.getBoundingClientRect();
      const parentRect = root.getBoundingClientRect();
      if (rowRect.top < parentRect.top || rowRect.bottom > parentRect.bottom) {
        row.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }

  return { root, setSentences, setActive };
}
